"use client";

import { motion } from "framer-motion";
import type { MotionValue } from "framer-motion";

import { cn } from "@/lib/utils";

import type { UnderwritingStep } from "./underwriting-engine";
import {
  UnderwritingEngineBox,
  UnderwritingEngineCard,
  UnderwritingEngineGlass,
} from "./underwriting-engine-card";
import type {
  EngineCardVariant,
  EngineGlassVariant,
} from "./underwriting-engine-card";

export interface EngineCardMotion {
  y: MotionValue<number>;
}

// Visual config per layer, bottom → top. Index matches step index (01/02/03).
const LAYERS: {
  variant: EngineCardVariant;
  /** Resting offset from the stack base, in px. */
  bottom: number;
  z: number;
}[] = [
  // Resting offsets from the assembled Figma stack (group 7617-32831):
  // colour layers 46px apart so each glass sheet overlaps the card below
  // and the backdrop blur picks up its colour.
  { variant: "orange", bottom: 0, z: 10 },
  { variant: "magenta", bottom: 46, z: 20 },
  { variant: "blue", bottom: 92, z: 30 },
];

// Transparent glass sheets interleaved with the layers (Figma "Stroke + Blur",
// group 7617-32831): one above each colour layer, midway to the next; the top
// sheet (above blue) carries the embossed wordmark. Each sheet shares its
// colour layer's motion value so the pair drops in as a unit.
const GLASS: {
  variant: EngineGlassVariant;
  bottom: number;
  z: number;
  /** Index of the colour layer (and step) this sheet is synced to. */
  layer: number;
  /** Reflection of the neighbouring layers' colours (bottom → top). */
  tint: string;
}[] = [
  // Tints paint inside the rotated diamond shell, so the angle is
  // pre-transformed: 45deg in square space renders screen bottom→top,
  // putting the stronger neighbour colour at the exposed bottom sliver.
  {
    variant: "mid",
    bottom: 24,
    z: 15,
    layer: 0,
    // Between orange and magenta.
    tint: "linear-gradient(45deg, rgba(255,96,64,0.3), rgba(201,74,214,0.24))",
  },
  {
    variant: "mid",
    bottom: 70,
    z: 25,
    layer: 1,
    // Between magenta and blue.
    tint: "linear-gradient(45deg, rgba(201,74,214,0.3), rgba(0,120,255,0.24))",
  },
  {
    variant: "top",
    bottom: 116,
    z: 35,
    layer: 2,
    // Caps the blue layer — cyan sheen like the Figma composite's top sheet.
    tint: "linear-gradient(45deg, rgba(0,120,255,0.3), rgba(64,208,255,0.24))",
  },
];

const STACK_W = 340;
const CARD_H = Math.round(STACK_W * (174 / 337)); // ≈ 176
const BADGE = 24;
const TOP_OFFSET = Math.max(
  ...LAYERS.map((l) => l.bottom),
  ...GLASS.map((g) => g.bottom)
);

interface UnderwritingEngineVisualProps {
  steps: UnderwritingStep[];
  activeStep: number;
  /** Per-card motion values (animated mode). Omit for the static render. */
  cards?: EngineCardMotion[];
  /**
   * Per-glass motion values — the sheets drop in BETWEEN the colour layers
   * (card 1 → glass 1 → card 2 → …). Falls back to riding the colour
   * layer's motion when omitted.
   */
  glassCards?: EngineCardMotion[];
  /** Fade-in of the closing glass box — the final beat. */
  box?: { veil: MotionValue<number> };
  /** Jump directly to a step — same handler as the waveform scrubber. */
  onStepSelect?: (stepIndex: number) => void;
  className?: string;
}

export function UnderwritingEngineVisual({
  steps,
  activeStep,
  cards,
  glassCards,
  box,
  onStepSelect,
  className,
}: UnderwritingEngineVisualProps) {
  return (
    <div
      className={cn("relative", className)}
      style={{ width: STACK_W + 64, height: TOP_OFFSET + CARD_H + 24 }}
    >
      {/* Vertical guide lines aligned to the diamonds' left vertex / centre /
          right vertex (animated/desktop only). Tall + centred so they read as
          full-height once the panel clips them. */}
      {cards && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
        >
          {[0, STACK_W / 2, STACK_W].map((x) => (
            <div
              key={x}
              className="border-grey-100 absolute top-1/2 h-[900px] w-0 -translate-y-1/2 border-l border-dashed"
              style={{ left: x }}
            />
          ))}
        </div>
      )}

      {/* Card stack */}
      <div className="absolute bottom-0 left-0" style={{ width: STACK_W }}>
        {LAYERS.map((layer, i) => {
          const motionValues = cards?.[i];
          const common = {
            className: "absolute left-0 will-change-transform",
            style: {
              width: STACK_W,
              bottom: layer.bottom,
              zIndex: layer.z,
            } as const,
          };
          const card = <UnderwritingEngineCard variant={layer.variant} />;

          return motionValues ? (
            <motion.div
              key={layer.variant}
              className={common.className}
              style={{ ...common.style, y: motionValues.y }}
            >
              {card}
            </motion.div>
          ) : (
            <div
              key={layer.variant}
              className={common.className}
              style={common.style}
            >
              {card}
            </div>
          );
        })}

        {/* Glass sheets — same full-width boxes as the colour cards. */}
        {GLASS.map((glass, i) => {
          const motionValues = glassCards?.[i] ?? cards?.[glass.layer];
          const common = {
            className: "absolute left-0 will-change-transform",
            style: {
              width: STACK_W,
              bottom: glass.bottom,
              zIndex: glass.z,
            } as const,
          };
          const sheet = (
            <UnderwritingEngineGlass
              variant={glass.variant}
              tint={glass.tint}
            />
          );

          return motionValues ? (
            <motion.div
              key={`${glass.variant}-${i}`}
              className={common.className}
              style={{ ...common.style, y: motionValues.y }}
            >
              {sheet}
            </motion.div>
          ) : (
            <div
              key={`${glass.variant}-${i}`}
              className={common.className}
              style={common.style}
            >
              {sheet}
            </div>
          );
        })}

        {/* Glass box — the final beat fades ONE rounded hexagon over the
            stack (Figma 7617:32860). Its interior gradients are painted and
            self-blurred, so it fully covers the layers behind it; no
            backdrop-filter, plain opacity fade. */}
        <div
          className="absolute"
          style={{
            left: 0,
            width: STACK_W,
            bottom: 3,
            height: 286,
            zIndex: 38,
          }}
        >
          <UnderwritingEngineBox
            veilOpacity={box?.veil}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      </div>

      {/* Number badges, aligned to each layer's centre */}
      <div
        className="absolute right-0 bottom-0 z-40"
        style={{ width: BADGE }}
        role={onStepSelect ? "group" : undefined}
        aria-label={onStepSelect ? "Underwriting steps" : undefined}
      >
        {LAYERS.map((layer, i) => {
          const isActive = i === activeStep;
          const step = steps[i];
          const badgeClassName = cn(
            "shadow-ds-xs absolute right-0 flex items-center justify-center rounded-full bg-white text-[10px] font-medium transition-colors duration-200",
            isActive ? "border-grey-200 text-grey-950 border" : "text-grey-500",
            onStepSelect &&
              "hover:border-grey-200 hover:text-grey-950 focus-visible:ring-grey-950 focus-visible:ring-offset-grey-0 cursor-pointer focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          );
          const badgeStyle = {
            width: BADGE,
            height: BADGE,
            bottom: layer.bottom + CARD_H / 2 - BADGE / 2,
          } as const;

          if (onStepSelect) {
            return (
              <button
                key={layer.variant}
                type="button"
                aria-current={isActive ? "step" : undefined}
                aria-label={
                  step
                    ? `Go to step ${step.number}: ${step.eyebrow}`
                    : `Go to step ${i + 1}`
                }
                onClick={() => onStepSelect(i)}
                className={badgeClassName}
                style={badgeStyle}
              >
                {step?.number}
              </button>
            );
          }

          return (
            <div
              key={layer.variant}
              aria-current={isActive ? "step" : undefined}
              className={badgeClassName}
              style={badgeStyle}
            >
              {step?.number}
            </div>
          );
        })}
      </div>
    </div>
  );
}
